// BigQuery Search Service - connects to our Cloud Functions API
const BIGQUERY_API_BASE = 'https://us-central1-gla-platform-2024.cloudfunctions.net';

/**
 * Search movies using BigQuery-powered Cloud Functions
 */
export async function searchMoviesFromBigQuery(query, filters = {}) {
  try {
    console.log('🔍 BigQuery search:', { query, filters });
    
    const params = new URLSearchParams({
      q: query || '',
      limit: filters.limit || 50
    });

    if (filters.yearMin) params.append('yearMin', filters.yearMin);
    if (filters.yearMax) params.append('yearMax', filters.yearMax);

    // Use the main search endpoint
    const response = await fetch(`${BIGQUERY_API_BASE}/searchMovies?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Search failed');
    }

    console.log('✅ BigQuery search results:', result.data);
    
    // Use the actual movie data from the search endpoint
    const movies = result.data.map(movie => ({
      id: movie.id,
      title: movie.title,
      year: movie.year,
      type: 'movie',
      ratings: {
        imdb: movie.ratings?.imdb || 0
      },
      genres: movie.genres || [],
      plot: movie.plot || '',
      poster: movie.poster || null,
      poster_width: movie.poster_width || null,
      poster_height: movie.poster_height || null,
      poster_caption: movie.poster_caption || null,
      director: movie.director,
      writer: movie.writer,
      runtime_minutes: movie.runtime_minutes,
      data_source: movie.data_source
    }));
    
    return {
      results: movies,
      total: movies.length,
      queryTime: Date.now()
    };

  } catch (error) {
    console.error('❌ BigQuery search failed:', error);
    throw new Error(`BigQuery search failed: ${error.message}`);
  }
}

/**
 * Get movie details from BigQuery
 */
export async function getMovieDetailsFromBigQuery(movieId) {
  try {
    console.log('🎬 Getting movie details:', movieId);
    
    const response = await fetch(`${BIGQUERY_API_BASE}/getMovieDetails?movieId=${movieId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Movie not found');
    }

    console.log('✅ Movie details:', result.data);
    return result.data;

  } catch (error) {
    console.error('❌ Movie details failed:', error);
    throw new Error(`Movie details failed: ${error.message}`);
  }
}

/**
 * Search talent using BigQuery-powered Cloud Functions
 */
export async function searchTalentFromBigQuery(query, filters = {}) {
  try {
    console.log('🔍 BigQuery talent search:', { query, filters });
    
    const params = new URLSearchParams({
      q: query || '',
      limit: filters.limit || 50
    });

    const response = await fetch(`${BIGQUERY_API_BASE}/searchTalent?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Talent search failed');
    }

    console.log('✅ BigQuery talent search results:', result.data);
    
    const talent = result.data.map(person => ({
      id: person.id,
      name: person.name,
      type: 'talent',
      known_for: person.known_for || [],
      biography: person.biography || '',
      data_source: person.data_source
    }));
    
    return {
      results: talent,
      total: talent.length,
      queryTime: Date.now()
    };

  } catch (error) {
    console.error('❌ BigQuery talent search failed:', error);
    throw new Error(`Talent search failed: ${error.message}`);
  }
}

/**
 * Get search suggestions from BigQuery
 */
export async function getSearchSuggestionsFromBigQuery(query, limit = 10) {
  try {
    if (!query || query.length < 2) {
      return { suggestions: [] };
    }
    
    console.log('💡 Getting search suggestions:', query);
    
    const response = await fetch(`${BIGQUERY_API_BASE}/getSearchSuggestions?q=${encodeURIComponent(query)}&limit=${limit}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Suggestions failed');
    }

    console.log('✅ Search suggestions:', result.data);
    return result.data;

  } catch (error) {
    console.error('❌ Search suggestions failed:', error);
    return { suggestions: [] };
  }
}

/**
 * Get database statistics
 */
export async function getDatabaseStatsFromBigQuery() {
  try {
    console.log('📊 Getting database stats');
    
    const response = await fetch(`${BIGQUERY_API_BASE}/getDatabaseStats`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Stats failed');
    }

    console.log('✅ Database stats:', result.data);
    return result.data;

  } catch (error) {
    console.error('❌ Database stats failed:', error);
    throw new Error(`Database stats failed: ${error.message}`);
  }
}

/**
 * Check BigQuery API health
 */
export async function checkBigQueryHealth() {
  try {
    const response = await fetch(`${BIGQUERY_API_BASE}/healthCheckBigQuery`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('❤️ BigQuery API Health:', result);
    return result;

  } catch (error) {
    console.error('❌ BigQuery health check failed:', error);
    throw error;
  }
}

/**
 * Combined search function that searches both movies and talent
 * For now, we only search movies since talent data isn't fully populated
 */
export async function searchContent(query, filters = {}) {
  try {
    console.log('🔍 Combined content search:', { query, filters });
    
    // Search both movies and talent in parallel
    const [movieResults, talentResults] = await Promise.all([
      searchMoviesFromBigQuery(query, filters),
      searchTalentFromBigQuery(query, filters)
    ]);
    
    // Combine results
    const allResults = [
      ...movieResults.results.map(movie => ({
        ...movie,
        type: 'movie'
      })),
      ...talentResults.results.map(talent => ({
        ...talent,
        type: 'talent'
      }))
    ];
    
    console.log('✅ Combined search results:', { 
      movies: movieResults.results.length, 
      talent: talentResults.results.length,
      total: allResults.length 
    });
    
    return {
      results: allResults,
      movies: movieResults.results,
      talent: talentResults.results,
      total: allResults.length,
      queryTime: Date.now()
    };

  } catch (error) {
    console.error('❌ Combined search failed:', error);
    throw new Error(`Search failed: ${error.message}`);
  }
}
